import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createSubscriptionCheckout,
  createPurchaseCheckout,
  openCustomerPortal as apiOpenCustomerPortal,
} from '../Utilities/entitlementsApi';
import useEntitlementsStore from '../Utilities/entitlementsStore';
import { trackCheckoutStarted, trackCheckoutCompleted } from '../Utilities/analytics';

/** Maps a credit-pack selection to its server-authorized product type. */
const CREDIT_PACK_PRODUCTS = {
  '100': 'CREDIT_PACK',
  '250': 'CREDIT_PACK_250',
};

/** Maps an entity type to its simple-pricing one-time report product type. */
const REPORT_PRODUCTS = {
  BIRTH_CHART: 'NATAL_REPORT',
  RELATIONSHIP: 'RELATIONSHIP_REPORT',
};

/**
 * Hook to handle Stripe checkout flows and post-checkout return
 * @param {Object} user - The user object
 * @param {Function} onSuccess - Callback on successful purchase/subscription
 * @returns {Object} Checkout functions and state
 */
export function useCheckout(user, onSuccess) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const location = useLocation();
  const navigate = useNavigate();
  const refreshAfterPurchase = useEntitlementsStore((state) => state.refreshAfterPurchase);

  // Handle post-checkout URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const upgraded = params.get('upgraded');
    const purchased = params.get('purchased');
    const purchaseType = params.get('type');
    const entityId = params.get('entityId');

    if (upgraded === 'true' || purchased === 'true') {
      // Track completed checkout
      trackCheckoutCompleted(
        upgraded === 'true' ? 'subscription' : 'purchase',
        purchaseType
      );

      // Show success message
      if (upgraded === 'true') {
        setSuccessMessage('Welcome to Plus! Your 3 included reports are ready.');
      } else if (purchased === 'true') {
        const typeMessages = {
          NATAL_REPORT: 'Natal report unlocked! Plus 5 questions to discuss it.',
          RELATIONSHIP_REPORT: 'Relationship report unlocked! Plus 5 questions to discuss it.',
          BIRTH_CHART: 'Birth chart analysis unlocked!', // Legacy credit model
          RELATIONSHIP: 'Relationship analysis unlocked!', // Legacy credit model
          QUESTION_PACK: 'Question pack added to your account!', // Legacy
          CREDIT_PACK: '100 credits added to your account!', // Legacy
          CREDIT_PACK_250: '250 credits added to your account!', // Legacy
        };
        setSuccessMessage(typeMessages[purchaseType] || 'Purchase successful!');
      }
      setShowSuccessToast(true);

      // Refresh entitlements after Stripe webhook processing. Poll until the
      // purchase is reflected (report unlocked, or plan now Plus) so a slow
      // webhook is caught without a manual refresh.
      if (user?._id) {
        const REPORT_ENTITY_TYPE = { NATAL_REPORT: 'BIRTH_CHART', RELATIONSHIP_REPORT: 'RELATIONSHIP' };
        const entityType = REPORT_ENTITY_TYPE[purchaseType];
        let until = null;
        if (upgraded === 'true') {
          until = (state) => state.plan === 'PLUS';
        } else if (purchased === 'true' && entityType && entityId) {
          until = (state) => state.isAnalysisUnlocked(entityType, entityId);
        }
        refreshAfterPurchase(user._id, { delayMs: 2000, until });
      }

      // Clean up URL parameters
      const newParams = new URLSearchParams(location.search);
      newParams.delete('upgraded');
      newParams.delete('purchased');
      newParams.delete('type');
      newParams.delete('entityId');
      newParams.delete('session_id');

      const newSearch = newParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      navigate(newUrl, { replace: true });

      // Call success callback
      if (onSuccess) {
        onSuccess(upgraded === 'true' ? 'subscription' : 'purchase');
      }

      // Auto-hide toast after 5 seconds
      setTimeout(() => {
        setShowSuccessToast(false);
      }, 5000);
    }
  }, [location.search, user?._id, navigate, location.pathname, onSuccess, refreshAfterPurchase]);

  /**
   * Build success/cancel URLs for checkout
   */
  const buildCheckoutUrls = useCallback(
    (purchaseType = null, entityId = null) => {
      const baseUrl = window.location.origin;
      const currentPath = location.pathname;

      let successParams = purchaseType
        ? `purchased=true&type=${purchaseType}`
        : 'upgraded=true';

      if (entityId) {
        successParams += `&entityId=${entityId}`;
      }

      return {
        successUrl: `${baseUrl}${currentPath}?${successParams}`,
        cancelUrl: `${baseUrl}${currentPath}?checkout=cancelled`,
      };
    },
    [location.pathname]
  );

  /**
   * Start Plus subscription checkout
   */
  const startSubscription = useCallback(async () => {
    if (!user?._id) {
      setError('Please sign in to subscribe');
      return { success: false, error: 'Not signed in' };
    }

    trackCheckoutStarted('subscription');
    setIsLoading(true);
    setError(null);

    try {
      const { successUrl, cancelUrl } = buildCheckoutUrls();
      const result = await createSubscriptionCheckout(user._id, successUrl, cancelUrl);

      if (result?.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
        return { success: true };
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Error starting subscription checkout:', err);
      setError(err.message || 'Failed to start checkout');
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  }, [user?._id, buildCheckoutUrls]);

  /**
   * Purchase analysis for a specific entity
   * @param {string} entityType - 'BIRTH_CHART' | 'RELATIONSHIP'
   * @param {string} entityId - ID of the entity to unlock
   */
  const purchaseAnalysis = useCallback(
    async (entityType, entityId) => {
      if (!user?._id) {
        setError('Please sign in to purchase');
        return { success: false, error: 'Not signed in' };
      }

      if (!entityType || !entityId) {
        setError('Invalid analysis type or ID');
        return { success: false, error: 'Invalid parameters' };
      }

      trackCheckoutStarted(entityType);
      setIsLoading(true);
      setError(null);

      try {
        const { successUrl, cancelUrl } = buildCheckoutUrls(entityType, entityId);
        const result = await createPurchaseCheckout(
          user._id,
          entityType,
          entityId,
          successUrl,
          cancelUrl
        );

        if (result?.checkoutUrl) {
          // Redirect to Stripe checkout
          window.location.href = result.checkoutUrl;
          return { success: true };
        } else {
          throw new Error('No checkout URL received');
        }
      } catch (err) {
        console.error('Error starting purchase checkout:', err);
        setError(err.message || 'Failed to start checkout');
        setIsLoading(false);
        return { success: false, error: err.message };
      }
    },
    [user?._id, buildCheckoutUrls]
  );

  /**
   * Purchase a one-time full report (simple pricing).
   * @param {string} entityType - 'BIRTH_CHART' | 'RELATIONSHIP'
   * @param {string} entityId - the chart / composite-chart id to unlock
   */
  const purchaseReport = useCallback(
    async (entityType, entityId) => {
      if (!user?._id) {
        setError('Please sign in to purchase');
        return { success: false, error: 'Not signed in' };
      }

      const productType = REPORT_PRODUCTS[entityType];
      if (!productType || !entityId) {
        setError('Invalid report type or ID');
        return { success: false, error: 'Invalid parameters' };
      }

      trackCheckoutStarted(productType);
      setIsLoading(true);
      setError(null);

      try {
        // Remember what was purchased so the page can auto-start the analysis
        // on return (survives the Stripe redirect via sessionStorage).
        try {
          sessionStorage.setItem(
            'ag_pending_report_start',
            JSON.stringify({ entityType, entityId })
          );
        } catch (e) { /* storage unavailable — auto-start just won't fire */ }

        const { successUrl, cancelUrl } = buildCheckoutUrls(productType, entityId);
        const result = await createPurchaseCheckout(
          user._id,
          productType,
          entityId,
          successUrl,
          cancelUrl
        );

        if (result?.checkoutUrl) {
          window.location.href = result.checkoutUrl;
          return { success: true };
        }
        throw new Error('No checkout URL received');
      } catch (err) {
        console.error('Error starting report checkout:', err);
        setError(err.message || 'Failed to start checkout');
        setIsLoading(false);
        return { success: false, error: err.message };
      }
    },
    [user?._id, buildCheckoutUrls]
  );

  /**
   * Purchase a credit pack.
   * @param {'100'|'250'} [packId='100'] - which pack to buy. '100' = $10 / 100 credits,
   *   '250' = $20 / 250 credits.
   */
  const purchaseCreditPack = useCallback(async (packId = '100') => {
    if (!user?._id) {
      setError('Please sign in to purchase');
      return { success: false, error: 'Not signed in' };
    }

    const productType = CREDIT_PACK_PRODUCTS[packId] || CREDIT_PACK_PRODUCTS['100'];

    trackCheckoutStarted(productType);
    setIsLoading(true);
    setError(null);

    try {
      const { successUrl, cancelUrl } = buildCheckoutUrls(productType);
      const result = await createPurchaseCheckout(
        user._id,
        productType,
        null,
        successUrl,
        cancelUrl
      );

      if (result?.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
        return { success: true };
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Error starting credit pack checkout:', err);
      setError(err.message || 'Failed to start checkout');
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  }, [user?._id, buildCheckoutUrls]);

  /**
   * Open Stripe customer portal for subscription management
   */
  const openCustomerPortal = useCallback(async () => {
    if (!user?._id) {
      setError('Please sign in to manage subscription');
      return { success: false, error: 'Not signed in' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const returnUrl = `${window.location.origin}${location.pathname}`;
      const result = await apiOpenCustomerPortal(user._id, returnUrl);

      if (result?.portalUrl) {
        // Redirect to Stripe portal
        window.location.href = result.portalUrl;
        return { success: true };
      } else {
        throw new Error('No portal URL received');
      }
    } catch (err) {
      console.error('Error opening customer portal:', err);
      setError(err.message || 'Failed to open billing portal');
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  }, [user?._id, location.pathname]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Dismiss success toast
   */
  const dismissSuccessToast = useCallback(() => {
    setShowSuccessToast(false);
  }, []);

  return {
    // State
    isLoading,
    error,
    showSuccessToast,
    successMessage,

    // Actions
    startSubscription,
    purchaseAnalysis,
    purchaseReport,
    purchaseCreditPack,
    openCustomerPortal,
    clearError,
    dismissSuccessToast,
  };
}

export default useCheckout;
